import { cliente } from '@/lib/cliente';
import type { Periodo } from '@adelante/shared';
import { obtenerRangoPeriodo } from '@adelante/shared';
import { useQuery } from '@tanstack/react-query';

function extraer<T>(res: { data: T | null; error: { mensaje: string } | null }): T {
  if (!res.data) throw new Error(res.error?.mensaje ?? 'Error del servidor');
  return res.data;
}

function paramsDePeriodo(p: Periodo) {
  const r = obtenerRangoPeriodo(p);
  return {
    desdeAnio: r.desdeAnio,
    desdeMes: r.desdeMes,
    hastaAnio: r.hastaAnio,
    hastaMes: r.hastaMes,
  };
}

/**
 * Clave estable para React Query basada en el rango efectivo del período
 * (no el modo). Así, si el usuario alterna entre "Mes Mayo" y "Rango May-May"
 * (mismo rango efectivo), se reusa el cache.
 */
function claveRango(p: Periodo) {
  const r = obtenerRangoPeriodo(p);
  return [r.desdeAnio, r.desdeMes, r.hastaAnio, r.hastaMes] as const;
}

export function useResumen(periodo: Periodo) {
  return useQuery({
    queryKey: ['utilidades-resumen', ...claveRango(periodo)],
    queryFn: () => cliente.utilidades.resumen(paramsDePeriodo(periodo)).then(extraer),
  });
}

export function useIngresos(periodo: Periodo) {
  return useQuery({
    queryKey: ['utilidades-ingresos', ...claveRango(periodo)],
    queryFn: () => cliente.utilidades.ingresos(paramsDePeriodo(periodo)).then(extraer),
  });
}

export function useDetalle(periodo: Periodo, pagina: number, busqueda?: string) {
  return useQuery({
    queryKey: ['utilidades-detalle', ...claveRango(periodo), pagina, busqueda],
    queryFn: () =>
      cliente.utilidades
        .detalle({
          ...paramsDePeriodo(periodo),
          pagina,
          busqueda: busqueda || undefined,
        })
        .then(extraer),
  });
}

export function useNeta(periodo: Periodo) {
  return useQuery({
    queryKey: ['utilidades-neta', ...claveRango(periodo)],
    queryFn: () => cliente.utilidades.neta(paramsDePeriodo(periodo)).then(extraer),
  });
}

export function useMovimientos(periodo: Periodo) {
  return useQuery({
    queryKey: ['utilidades-movimientos', ...claveRango(periodo)],
    queryFn: () => cliente.utilidades.movimientos(paramsDePeriodo(periodo)).then(extraer),
  });
}

export function useIndirectos(periodo: Periodo) {
  return useQuery({
    queryKey: ['utilidades-indirectos', ...claveRango(periodo)],
    queryFn: () => cliente.utilidades.indirectos(paramsDePeriodo(periodo)).then(extraer),
  });
}

export function useObras() {
  return useQuery({
    queryKey: ['utilidades-obras'],
    queryFn: () => cliente.utilidades.obras().then(extraer),
    staleTime: 1000 * 60 * 30, // 30 min — los datos cambian con el ETL nightly
  });
}
